export type MonsterState = 'wild_passive' | 'wild_hostile' | 'tamed' | 'working';
export type MonsterJob = 'idle' | 'foraging' | 'guarding' | 'crafting';

export interface MonsterAIOptions {
  id: string;
  name: string;
  species: string;
  affinityThreshold?: number;
  initialHp?: number;
  position?: { x: number; y: number; z: number };
}

export interface FeedInput {
  itemId: string;
  name: string;
  affinityGain: number;
}

export interface FeedResult {
  tamed: boolean;
  currentAffinity: number;
}

export interface MonsterUpdateResult {
  action: string;
  yieldItem?: { itemId: string; name: string; quantity: number };
}

export class MonsterAI {
  public readonly id: string;
  public readonly name: string;
  public readonly species: string;
  public readonly affinityThreshold: number;

  public state: MonsterState = 'wild_passive';
  public job: MonsterJob = 'idle';
  public affinity: number = 0;
  public maxHp: number;
  public currentHp: number;
  public position: { x: number; y: number; z: number };

  private workTimer: number = 0;
  private readonly workCycleSec: number = 5.0;

  constructor(options: MonsterAIOptions) {
    this.id = options.id;
    this.name = options.name;
    this.species = options.species;
    this.affinityThreshold = options.affinityThreshold ?? 50;
    this.maxHp = options.initialHp ?? 100;
    this.currentHp = this.maxHp;
    this.position = options.position ? { ...options.position } : { x: 0, y: 0, z: 0 };
  }

  public get isTamed(): boolean {
    return this.state === 'tamed' || this.state === 'working';
  }

  public feed(item: FeedInput): FeedResult {
    this.affinity = Math.min(100, this.affinity + item.affinityGain);

    if (!this.isTamed && this.affinity >= this.affinityThreshold) {
      this.state = 'tamed';
      this.job = 'idle';
      return { tamed: true, currentAffinity: this.affinity };
    }

    return { tamed: this.isTamed, currentAffinity: this.affinity };
  }

  public assignJob(job: MonsterJob): boolean {
    if (!this.isTamed) {
      return false;
    }

    this.job = job;
    this.state = job === 'idle' ? 'tamed' : 'working';
    this.workTimer = 0;
    return true;
  }

  public update(dt: number): MonsterUpdateResult {
    if (this.state === 'working' && this.job === 'foraging') {
      this.workTimer += dt;
      if (this.workTimer >= this.workCycleSec) {
        this.workTimer = 0;
        return {
          action: 'foraging_complete',
          yieldItem: { itemId: 'herb_vitality', name: 'Vitality Herb', quantity: 1 }
        };
      }
      return { action: 'foraging' };
    }

    if (this.state === 'working' && this.job === 'guarding') {
      return { action: 'guarding' };
    }

    return { action: this.state };
  }
}
